import {
  Edit,
  Face3,
  Face6,
  NotInterested,
  Visibility,
} from '@mui/icons-material';
import {
  Avatar,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { Patient } from 'api/models';
import RouterLink from 'components/RouterLink/RouterLink';
import useHasAccess from 'hooks/useHasAccess';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Permission } from 'state/permissionState';
import { GeneralIds, RoutePaths } from 'types/types';
import { getDateString, getRoutePath } from 'utils';

function getGenderIcon(gender?: string) {
  const sx = { mr: 0.3 };
  if (gender === 'male') {
    return <Face6 fontSize="large" sx={sx} />;
  }

  if (gender === 'female') {
    return <Face3 fontSize="large" sx={sx} />;
  }

  return <NotInterested fontSize="large" sx={sx} />;
}

function getPatientName(patient?: Patient) {
  const { firstName, lastName, titles } = patient ?? {};
  const title = titles ? titles.replaceAll('.', '') + '.' : '';
  return `${title} ${lastName ?? ''} ${firstName ?? ''}`;
}

function KeyValueItem({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | JSX.Element;
}) {
  return (
    <Box display="flex" mr={1} ml={1}>
      <Box mr={0.3}>{label}:</Box>
      <Typography fontWeight={'bold'}>{value}</Typography>
    </Box>
  );
}

export function PatientCard({
  patient,
  hideControls = false,
}: {
  patient?: Patient;
  hideControls?: boolean;
}) {
  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const navigate = useNavigate();
  const canEditPatients = useHasAccess([Permission.WRITE_PATIENT]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { id, birthDate, gender, identifiers, patientProjectId } =
    patient ?? {};

  return (
    <Card variant="outlined">
      {!patient && <LinearProgress />}
      <CardContent>
        {!hideControls && (
          <Box
            display={'flex'}
            justifyContent={'end'}
            position={'absolute'}
            right={'30px'}
          >
            <RouterLink
              to={getRoutePath(RoutePaths.patientViewer, {
                patientId: id,
                projectId,
              })}
            >
              <IconButton size="small">
                <Tooltip title={t('general.view')}>
                  <Visibility fontSize="small" />
                </Tooltip>
              </IconButton>
            </RouterLink>

            <IconButton
              size="small"
              disabled={!canEditPatients}
              onClick={() =>
                navigate(
                  getRoutePath(RoutePaths.patientEditor, {
                    patientId: id,
                    projectId,
                  }),
                )
              }
            >
              <Tooltip title={t('general.edit')}>
                <Edit fontSize="small" />
              </Tooltip>
            </IconButton>
          </Box>
        )}

        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          {getGenderIcon(gender)}
          <Typography variant="h5" fontWeight={'bold'} ml={1}>
            {getPatientName(patient)}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems={isMobile ? 'start' : 'center'}
          flexDirection={isMobile ? 'column' : 'row'}
          mt={1}
        >
          <Chip
            key={patientProjectId}
            label={patientProjectId}
            avatar={<Avatar>{`ID`}</Avatar>}
            sx={{ mr: 1 }}
          />
          <Divider orientation="vertical" flexItem />
          {birthDate && (
            <>
              <KeyValueItem
                label={t('general.birthDate')}
                value={getDateString(birthDate)}
              />
              <Divider orientation="vertical" flexItem />
            </>
          )}
          {identifiers && identifiers.length > 0 && (
            <Box display={'flex'} flexDirection={'row'} ml={1}>
              {identifiers.map(identifier => (
                <KeyValueItem
                  label={t(`general.idTypes.shortened.${identifier.type}`)}
                  value={identifier.id}
                />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
